import {
    Image,
    makeStyles,
    mergeClasses,
    Skeleton,
    SkeletonItem,
    Text,
    tokens
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import testimonialService from "../../services/TestimonialService";
import ITestimonial from "../../models/testimonial/ITestimonial";
import IClient from "../../models/client/IClient";
import clientService from "../../services/ClientService";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Status from "../../enums/Status";
import { dolphinTokens } from "../../utils/Theme";
import { HtmlToPlainText } from "../../utils/Html";

const useStyles = makeStyles({
    container: {
        marginBottom: "3rem"
    },
    sectionHeader: {
        textAlign: "center"
    },
    blueBadge: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white",
        padding: "0.5rem 1.5rem",
        borderRadius: "50px",
        fontSize: "0.875rem",
        textTransform: "uppercase",
        display: "inline-block"
    },
    reviewCarousel: {
        position: "relative",
        padding: "2rem",
        display: "flex",
        justifyContent: "center"
    },
    reviewSlide: {
        display: "none",
        opacity: 0,
        transition: "opacity 0.5s ease-in-out",
        textAlign: "center",
        width: "75%",

        "&.active": {
            display: "block !important",
            opacity: "1 !important"
        },

        "& a": {
            color: dolphinTokens.colorLightBlue,
            textDecoration: "none"
        },

        "@media (max-width: 768px)": {
            width: "90%"
        }
    },
    speechBubble: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: "20px",
        padding: "2rem",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        position: "relative",
        marginBottom: "2rem",

        "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-15px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "0",
            height: "0",
            borderLeft: "15px solid transparent",
            borderRight: "15px solid transparent",
            borderTop: `15px solid ${tokens.colorNeutralBackground1}`,
            filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1))"
        },

        "@media (max-width: 768px)": {
            padding: "1.5rem"
        }
    },
    clientInfo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1rem",

        "& img": {
            height: "80px",
            width: "auto"
        }
    },
    skeletons: {
        flexDirection: "column",
        gap: tokens.spacingVerticalL,

        "& > div": {
            marginLeft: "auto",
            marginRight: "auto"
        },

        "& > :nth-child(1)": {
            width: "256px !important",
            marginBottom: "1.5rem !important"
        },

        "& > :nth-child(2)": {
            width: "70% !important"
        }
    }
});

type TestimonialProps = {
    testimonial: ITestimonial;
    client?: IClient;
    active: boolean;
};

const Testimonial = (props: TestimonialProps) => {
    const { testimonial, client, active } = props;

    const { t } = useTranslation("home");
    const styles = useStyles();

    const className = active
        ? mergeClasses(styles.reviewSlide, "active")
        : styles.reviewSlide;

    const content = HtmlToPlainText(testimonial.content);

    return (
        <div className={className}>
            <div className={styles.speechBubble}>
                <Text block size={500} align="center">
                    &ldquo;
                    {content.length < 170 ? (
                        <span>{content} </span>
                    ) : (
                        <span>{content.substring(0, 170)}... </span>
                    )}
                    <Link to="/testimonials">
                        {content.length > 170
                            ? t("testimonials.btnReadMore")
                            : t("testimonials.btnReadAll")}
                    </Link>
                    &rdquo;
                </Text>
            </div>

            <div className={styles.clientInfo}>
                {client?.logo?.url && (
                    <Image
                        src={client.logo.url}
                        alt={client.logo.altText}
                        fetchPriority="low"
                        loading="lazy"
                    />
                )}
                <Text as="p" size={400}>
                    {client?.name}
                </Text>
            </div>
        </div>
    );
};

export default function Testimonials() {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [clients, setClients] = useState<IClient[]>([]);
    const [activeTestimonial, setActiveTestimonial] = useState<number>(0);

    useEffect(() => {
        getTestimonials(setTestimonials, setClients, setStatus);
    }, []);

    const sortedTestimonials = useMemo(() => {
        return [...testimonials].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }, [testimonials]);

    useEffect(() => {
        if (sortedTestimonials.length === 0) {
            return;
        }

        const interval = setInterval(() => {
            setActiveTestimonial((prev) =>
                prev >= sortedTestimonials.length - 1 ? 0 : prev + 1
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [sortedTestimonials.length]);

    useEffect(() => {
        if (sortedTestimonials.length > 0) {
            setActiveTestimonial(0);
        }
    }, [sortedTestimonials.length]);

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Text as="h2" size={700} align="center" block>
                {t("testimonials.title")}
            </Text>

            {status === Status.LOADING && (
                <div className={styles.skeletons}>
                    <Skeleton>
                        <SkeletonItem size={128} shape="rectangle" />
                    </Skeleton>
                    <Skeleton>
                        <SkeletonItem size={72} shape="rectangle" />
                    </Skeleton>
                </div>
            )}

            {status === Status.LOAD_SUCCESS && (
                <div className={styles.reviewCarousel}>
                    {sortedTestimonials.map((testimonial, index) => (
                        <Testimonial
                            key={testimonial.id}
                            testimonial={testimonial}
                            client={clients.find((client) => client.id === testimonial.clientId)}
                            active={index === activeTestimonial}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

async function getTestimonials(
    setTestimonials: (items: ITestimonial[]) => void,
    setClients: (clients: IClient[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const [response, clients] = await Promise.all([
            testimonialService.getTestimonials(),
            clientService.getClients()
        ]);

        setTestimonials(response);
        setClients(clients);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}
